import { useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AWAITING_INCAPACITY_STATUSES,
  AWAITING_PERMISSION_STATUSES,
} from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import { AbsenceTypePicker } from '../AbsenceTypePicker';
import { useAbsenceScope } from '../../hooks/useAbsenceScope';
import { useAccess } from '../../hooks/useAccess';
import { useSession } from '../../providers/SessionProvider';
import { RequestsScreen } from '../Requests/RequestsScreen';
import { IncapacitiesScreen } from './IncapacitiesScreen';
import { PermissionsScreen } from './PermissionsScreen';
import { activeAbsenceTab, type AbsenceTab } from './absenceTab';
import styles from './AbsencesScreen.module.css';

/**
 * Mis ausencias.
 *
 * One destination holding the three flows an employee actually has. They are
 * genuinely different — vacation spends the balance, an IMSS leave never does,
 * and a permit may be counted in hours — so the picker is three cards carrying
 * a subtitle each, not a segmented control that would imply they are variants
 * of one thing.
 *
 * The counters are live: a badge only earns its place when something is waiting
 * on the person, which is why they count *pending* records rather than totals.
 */

export const AbsencesScreen = (): ReactNode => {
  const session = useSession();
  const { holds, featureOn } = useAccess();
  const absences = useAbsenceScope();
  const location = useLocation();
  /* Null hasta que la persona elige: mientras tanto manda `?tipo=`. */
  const [tab, setTab] = useState<AbsenceTab | null>(null);

  const pendingRequests = useQuery({
    queryKey: ['absences', 'pendingRequests', session.personId],
    queryFn: async () => {
      const result = await getServices().requests.count({
        personId: session.personId,
        statuses: ['Por revisar', 'En revisión', 'Visto bueno'],
      });
      return result.ok ? result.value : 0;
    },
  });

  /*
   * The tab badges count whatever the tab will show.
   *
   * Scoping them to the person while the list below shows the whole area made
   * the badge contradict the list it labels — an administrator saw «0» over two
   * certificates waiting on them.
   *
   * The status sets come from the vocabulary rather than being spelled here: the
   * sidebar's «Ausencias» pill is the sum of these three counters, and when the
   * two spellings drifted the pill read 2 over tabs that added to 3.
   */
  const listScope = absences.scope;

  const pendingIncapacities = useQuery({
    queryKey: ['absences', 'pendingIncapacities', listScope],
    enabled: featureOn('incapacidades'),
    queryFn: async () => {
      const result = await getServices().incapacities.count({
        ...listScope,
        statuses: AWAITING_INCAPACITY_STATUSES,
      });
      return result.ok ? result.value : 0;
    },
  });

  const pendingPermissions = useQuery({
    queryKey: ['absences', 'pendingPermissions', listScope],
    enabled: featureOn('permisos'),
    queryFn: async () => {
      const result = await getServices().permissions.count({
        ...listScope,
        statuses: AWAITING_PERMISSION_STATUSES,
      });
      return result.ok ? result.value : 0;
    },
  });

  const tabs: readonly {
    id: AbsenceTab;
    title: string;
    note: string;
    glyph: string;
    color: string;
    count: number;
    available: boolean;
  }[] = [
    {
      id: 'vacaciones',
      title: 'Vacaciones',
      note: 'Solicitudes y saldo',
      glyph: '☀',
      color: 'var(--color-brand)',
      count: pendingRequests.data ?? 0,
      // An area administrator does not file vacation requests, so this tab
      // would open on an empty "todavía no has pedido días" that is not about
      // them. Incapacidades becomes the landing tab instead.
      available: holds('solicitar'),
    },
    {
      id: 'incapacidades',
      title: 'Incapacidades',
      note: 'IMSS y certificados',
      glyph: '✚',
      color: 'var(--color-warning)',
      count: pendingIncapacities.data ?? 0,
      available: featureOn('incapacidades'),
    },
    {
      id: 'permisos',
      title: 'Home office y permisos',
      note: 'Remoto, horas, día personal',
      glyph: '⌂',
      color: 'var(--color-success)',
      count: pendingPermissions.data ?? 0,
      available: featureOn('permisos'),
    },
  ];

  const visible = tabs.filter((t) => t.available);
  const active = activeAbsenceTab({
    visible: visible.map((t) => t.id),
    chosen: tab,
    search: location.search,
  });

  return (
    <div className="flow">
      <div className={styles.eyebrow}>{holds('solicitar') ? 'Mis ausencias' : 'Ausencias del área'}</div>

      <AbsenceTypePicker options={visible} active={active} onSelect={setTab} />

      {active === 'vacaciones' && <RequestsScreen />}
      {active === 'incapacidades' && <IncapacitiesScreen />}
      {active === 'permisos' && <PermissionsScreen />}
    </div>
  );
};
